/**
 * React Query hooks for Platform Provider management
 *
 * Aligned with backend admin_router.py endpoints:
 *   prefix = /api/v1/admin
 *   providers → /platform-providers
 *   models    → /platform-models  (flat, with provider_id filter)
 *   health    → /platform-providers/{id}/health-check  (POST)
 *   policy    → /platform-providers/{id}/policy         (GET/PUT)
 *   summary   → /platform-providers/health-summary      (GET)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import type {
  PlatformProvider,
  PlatformProviderCreate,
  PlatformProviderEndpointResponse,
  PlatformProviderUpdate,
  PlatformModel,
  PlatformModelCreate,
  ProviderPolicy,
  ProviderPolicyUpdate,
} from '../types';

// =============================================================================
// API base
// =============================================================================

const ADMIN_BASE = '/api/v1/admin';

type MaybeWrappedList<T> =
  | { items?: T[] }
  | { data?: { items?: T[] } | T[] }
  | T[]
  | string
  | null
  | undefined;

function extractItems<T>(payload: MaybeWrappedList<T>): T[] {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (typeof payload === 'string') {
    // Some proxies/middlewares may return JSON as text despite content-type.
    try {
      const parsed = JSON.parse(payload) as MaybeWrappedList<T>;
      return extractItems(parsed);
    } catch {
      return [];
    }
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  if ('items' in payload && Array.isArray(payload.items)) {
    return payload.items;
  }

  if ('data' in payload) {
    const nested = payload.data;
    if (Array.isArray(nested)) {
      return nested;
    }
    if (nested && typeof nested === 'object' && 'items' in nested && Array.isArray(nested.items)) {
      return nested.items;
    }
  }

  return [];
}

// =============================================================================
// Query Keys
// =============================================================================

export const providerKeys = {
  all: ['platform-providers'] as const,
  list: () => [...providerKeys.all, 'list'] as const,
  detail: (id: string) => [...providerKeys.all, 'detail', id] as const,
  endpoint: (id: string) => [...providerKeys.all, 'endpoint', id] as const,
  models: (providerId: string) => [...providerKeys.all, 'models', providerId] as const,
  healthSummary: () => [...providerKeys.all, 'health-summary'] as const,
  policy: (providerId: string) => [...providerKeys.all, 'policy', providerId] as const,
  supportedTypes: () => [...providerKeys.all, 'supported-types'] as const,
};

// =============================================================================
// Supported Types (feature-flag aware)
// =============================================================================

export interface SupportedProviderType {
  value: string;
  label: string;
}

export interface SupportedTypesResponse {
  provider_types: SupportedProviderType[];
  feature_flags: {
    atlas_bedrock_enabled: boolean;
  };
}

/**
 * Fetch the list of provider types enabled on this deployment.
 * Backend: GET /api/v1/admin/platform-providers/supported-types
 */
export function useSupportedProviderTypes() {
  return useQuery({
    queryKey: providerKeys.supportedTypes(),
    queryFn: async () => {
      const response = await apiClient.get<{ data: SupportedTypesResponse }>(
        `${ADMIN_BASE}/platform-providers/supported-types`,
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes — changes only on redeploy
  });
}

// =============================================================================
// Provider Queries
// =============================================================================

/**
 * Fetch all platform providers.
 * Backend: GET /api/v1/admin/platform-providers
 * Response: { items: PlatformProvider[], total: number }
 */
export function usePlatformProviders(includeInactive = false) {
  return useQuery({
    queryKey: [...providerKeys.list(), { includeInactive }],
    queryFn: async () => {
      const response = await apiClient.get<MaybeWrappedList<PlatformProvider> | { items: PlatformProvider[]; total: number }>(
        `${ADMIN_BASE}/platform-providers`,
        { params: { active_only: !includeInactive } }
      );
      return extractItems<PlatformProvider>(response.data);
    },
  });
}

/**
 * Fetch single platform provider by ID.
 * Backend: GET /api/v1/admin/platform-providers/{id}
 */
export function usePlatformProvider(id: string) {
  return useQuery({
    queryKey: providerKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<PlatformProvider>(
        `${ADMIN_BASE}/platform-providers/${id}`
      );
      return response.data;
    },
    enabled: !!id,
  });
}

/**
 * Fetch the private endpoint for a single platform provider.
 * Backend: GET /api/v1/admin/platform-providers/{id}/endpoint
 */
export function usePlatformProviderEndpoint(id: string, enabled = true) {
  return useQuery({
    queryKey: providerKeys.endpoint(id),
    queryFn: async () => {
      const response = await apiClient.get<PlatformProviderEndpointResponse>(
        `${ADMIN_BASE}/platform-providers/${id}/endpoint`
      );
      return response.data;
    },
    enabled: !!id && enabled,
  });
}

/**
 * Fetch health summary for all providers.
 * Backend: GET /api/v1/admin/platform-providers/health-summary
 */
export function useProvidersHealthSummary(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: providerKeys.healthSummary(),
    queryFn: async () => {
      const response = await apiClient.get<{
        summary: Record<string, number>;
        total: number;
        providers: Array<{
          id: string;
          name: string;
          provider_type: string;
          status: string;
          last_health_check: string | null;
          consecutive_failures: number;
        }>;
      }>(`${ADMIN_BASE}/platform-providers/health-summary`);
      return response.data;
    },
    enabled: options?.enabled,
  });
}

// =============================================================================
// Provider Mutations
// =============================================================================

/**
 * Create new platform provider.
 * Backend: POST /api/v1/admin/platform-providers
 */
export function useCreatePlatformProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PlatformProviderCreate) => {
      const response = await apiClient.post<PlatformProvider>(
        `${ADMIN_BASE}/platform-providers`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
    },
  });
}

/**
 * Update existing platform provider.
 * Backend: PUT /api/v1/admin/platform-providers/{id}
 */
export function useUpdatePlatformProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: PlatformProviderUpdate }) => {
      const response = await apiClient.put<PlatformProvider>(
        `${ADMIN_BASE}/platform-providers/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
      queryClient.invalidateQueries({ queryKey: providerKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: providerKeys.endpoint(variables.id) });
    },
  });
}

/**
 * Delete (deactivate) platform provider.
 * Backend: DELETE /api/v1/admin/platform-providers/{id}
 */
export function useDeletePlatformProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`${ADMIN_BASE}/platform-providers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
    },
  });
}

/**
 * Trigger on-demand health check for a provider.
 * Backend: POST /api/v1/admin/platform-providers/{id}/health-check
 */
export function useTriggerHealthCheck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (providerId: string) => {
      const response = await apiClient.post<{
        provider_id: string;
        status: string;
        latency_ms: number | null;
        error: string | null;
        models: Array<{
          model_id: string;
          status: string;
          latency_ms: number | null;
          error: string | null;
        }>;
        overall_status: string;
      }>(`${ADMIN_BASE}/platform-providers/${providerId}/health-check`);
      return response.data;
    },
    onSuccess: (_, providerId) => {
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
      queryClient.invalidateQueries({ queryKey: providerKeys.detail(providerId) });
      queryClient.invalidateQueries({ queryKey: providerKeys.models(providerId) });
      queryClient.invalidateQueries({ queryKey: providerKeys.healthSummary() });
    },
  });
}

// =============================================================================
// Model Queries
// =============================================================================

/**
 * Fetch models for a platform provider.
 * Backend: GET /api/v1/admin/platform-models?provider_id={id}
 * Response: { items: PlatformModel[], total: number }
 */
export function useProviderModels(providerId: string, activeOnly = true) {
  return useQuery({
    queryKey: [...providerKeys.models(providerId), { activeOnly }],
    queryFn: async () => {
      const response = await apiClient.get<MaybeWrappedList<PlatformModel> | { items: PlatformModel[]; total: number }>(
        `${ADMIN_BASE}/platform-models`,
        { params: { provider_id: providerId, active_only: activeOnly, page_size: 100 } }
      );
      return extractItems<PlatformModel>(response.data);
    },
    enabled: !!providerId,
  });
}

// =============================================================================
// Model Mutations
// =============================================================================

/**
 * Create platform model.
 * Backend: POST /api/v1/admin/platform-models
 */
export function useCreateProviderModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      providerId,
      data,
    }: {
      providerId: string;
      data: PlatformModelCreate;
    }) => {
      const response = await apiClient.post<PlatformModel>(
        `${ADMIN_BASE}/platform-models`,
        { ...data, provider_id: providerId }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: providerKeys.models(variables.providerId),
      });
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
    },
  });
}

/**
 * Update platform model.
 * Backend: PUT /api/v1/admin/platform-models/{model_id}
 */
export function useUpdateProviderModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      providerId: _providerId,
      modelId,
      data,
    }: {
      providerId: string;
      modelId: string;
      data: Partial<PlatformModelCreate>;
    }) => {
      const response = await apiClient.put<PlatformModel>(
        `${ADMIN_BASE}/platform-models/${modelId}`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: providerKeys.models(variables.providerId),
      });
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
    },
  });
}

/**
 * Delete platform model.
 * Backend: DELETE /api/v1/admin/platform-models/{model_id}
 */
export function useDeleteProviderModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      providerId: _providerId,
      modelId,
    }: {
      providerId: string;
      modelId: string;
    }) => {
      await apiClient.delete(`${ADMIN_BASE}/platform-models/${modelId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: providerKeys.models(variables.providerId),
      });
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
    },
  });
}

// =============================================================================
// Provider Policy Hooks
// =============================================================================

/**
 * Fetch policy for a platform provider.
 * Backend: GET /api/v1/admin/platform-providers/{id}/policy
 */
export function useProviderPolicy(providerId: string) {
  return useQuery({
    queryKey: providerKeys.policy(providerId),
    queryFn: async () => {
      const response = await apiClient.get<ProviderPolicy>(
        `${ADMIN_BASE}/platform-providers/${providerId}/policy`
      );
      return response.data;
    },
    enabled: !!providerId,
  });
}

/**
 * Update policy for a platform provider.
 * Backend: PUT /api/v1/admin/platform-providers/{id}/policy
 */
export function useUpdateProviderPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      providerId,
      data,
    }: {
      providerId: string;
      data: ProviderPolicyUpdate;
    }) => {
      const response = await apiClient.put<ProviderPolicy>(
        `${ADMIN_BASE}/platform-providers/${providerId}/policy`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: providerKeys.policy(variables.providerId),
      });
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
    },
  });
}
